import { FC, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { Users } from 'lucide-react';
import { CourtierValidationDto, TunnelValideurDto } from 'api/funnel';
import { funnelsAPI } from 'services/swagger-api';
import { useDialog } from 'hooks/useDialog';
import { CheckboxComponent } from 'components/forms/Checkbox';
import { RadioGroupComponent } from 'components/forms/RadioGroup';
import { parseApiError } from 'shared/utils/apiValidationErrors';
import { ModalShell, ModalSection } from 'back-office/shared/components/ModalShell';
import { DASH } from '../funnelFormat';

type ScopeMode = 'all' | 'list';

const MODE_OPTIONS: { value: ScopeMode; label: string }[] = [
  { value: 'all', label: 'Tous les courtiers habilités' },
  { value: 'list', label: 'Une liste de courtiers' },
];

interface ScopeBroker {
  courtierId: string;
  nom: string;
  plateformes: string;
}

interface ValidatorScopeModalProps {
  tunnelId: string;
  valideur: TunnelValideurDto;
  courtiers: CourtierValidationDto[];
  onSuccess: () => void;
}

/** Restricts a validator to some authorised brokers, or opens it to all of them. */
export const ValidatorScopeModal: FC<ValidatorScopeModalProps> = ({
  tunnelId,
  valideur,
  courtiers,
  onSuccess,
}) => {
  const { closeDialog } = useDialog();
  const [mode, setMode] = useState<ScopeMode>(valideur.courtierIds.length === 0 ? 'all' : 'list');
  const [selected, setSelected] = useState<Set<string>>(() => new Set(valideur.courtierIds));
  const [isSubmitting, setIsSubmitting] = useState(false);

  // The scope targets brokers, not authorisations: one entry per broker, with every
  // platform through which it is authorised.
  const brokers = useMemo<ScopeBroker[]>(() => {
    const byId = new Map<string, { nom: string; plateformes: string[] }>();
    courtiers.forEach((courtier) => {
      const entry = byId.get(courtier.courtierId) ?? { nom: courtier.nom ?? DASH, plateformes: [] };
      entry.plateformes.push(courtier.plateformeNom ?? DASH);
      byId.set(courtier.courtierId, entry);
    });
    return Array.from(byId, ([courtierId, entry]) => ({
      courtierId,
      nom: entry.nom,
      plateformes: entry.plateformes.join(', '),
    })).sort((a, b) => a.nom.localeCompare(b.nom, 'fr'));
  }, [courtiers]);

  const toggle = (courtierId: string, checked: boolean) =>
    setSelected((current) => {
      const next = new Set(current);
      if (checked) next.add(courtierId);
      else next.delete(courtierId);
      return next;
    });

  const submit = async () => {
    // An empty list means every authorised broker.
    const courtierIds = mode === 'all' ? [] : Array.from(selected);

    setIsSubmitting(true);
    try {
      const response = await funnelsAPI.setFunnelValidatorBrokers({
        tunnelId,
        valideurId: valideur.id,
        setTunnelValideurCourtiersCommand: { courtierIds },
      });
      if (response.isSuccess) {
        toast.success('Périmètre enregistré');
        onSuccess();
        closeDialog();
      } else {
        toast.error(response.errorMessage || "L'enregistrement du périmètre a échoué");
      }
    } catch (error) {
      console.error('Enregistrement du périmètre impossible', error);
      const parsed = await parseApiError(error);
      toast.error(parsed.message || "L'enregistrement du périmètre a échoué");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ModalShell
      title={`Périmètre de ${valideur.nom ?? 'ce valideur'}`}
      subtitle="Courtiers dont il valide les devis"
      submit={{
        label: 'Sauvegarder',
        onClick: submit,
        disabled: isSubmitting || (mode === 'list' && selected.size === 0),
      }}
    >
      <ModalSection title="Périmètre" icon={<Users className="h-4 w-4" />}>
        <div className="flex flex-col gap-4">
          <RadioGroupComponent<ScopeMode>
            name="scopeMode"
            value={mode}
            options={MODE_OPTIONS}
            onChange={setMode}
          />

          {mode === 'list' &&
            (brokers.length === 0 ? (
              <p className="text-sm italic text-gray-500">
                Aucun courtier n'est habilité à ce tunnel.
              </p>
            ) : (
              <>
                <ul className="flex flex-col gap-2">
                  {brokers.map((broker) => (
                    <li key={broker.courtierId} className="flex items-center justify-between gap-3">
                      <CheckboxComponent
                        name={`courtier-${broker.courtierId}`}
                        label={broker.nom}
                        checked={selected.has(broker.courtierId)}
                        onChange={(checked) => toggle(broker.courtierId, checked)}
                      />
                      <span className="truncate text-xs text-gray-400">{broker.plateformes}</span>
                    </li>
                  ))}
                </ul>
                {selected.size === 0 && (
                  <p className="text-xs text-gray-500">Cochez au moins un courtier.</p>
                )}
              </>
            ))}
        </div>
      </ModalSection>
    </ModalShell>
  );
};
